// src/pages/PublicationPage.jsx (مسیرت هرچی هست، حفظ کن)
import { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../auth/AuthContext';

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(1)} ${sizes[i]}`;
}

export default function PublicationPage() {
  const { user, csrfToken } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [topic, setTopic] = useState('');
  const [fileFormat, setFileFormat] = useState('');
  const [minPages, setMinPages] = useState('');
  const [maxPages, setMaxPages] = useState('');
  const [sortTitle, setSortTitle] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const perPageOptions = [4, 8, 12, 16, 20];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/publication-categories', {
          withCredentials: true
        });
        setCategories(data);
      } catch (err) {
        console.error('Error fetching publication categories', err);
      }
    })();
  }, []);

  const fetchPublications = async () => {
    try {
      const { data } = await api.get('/api/publications', {
        params: {
          search,
          categoryId,
          topic,
          fileFormat,
          minPages,
          maxPages,
          sortTitle,
          page,
          pageSize
        },
        withCredentials: true
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('Error fetching publications', err);
    }
  };

  useEffect(() => {
    fetchPublications();
  }, [search, categoryId, topic, fileFormat, minPages, maxPages, sortTitle, page, pageSize]);

  const totalPages = useMemo(
    () => Math.ceil(total / pageSize),
    [total, pageSize]
  );

  const avgRating = ratings => {
    if (!ratings || ratings.length === 0) return '0.0';
    const map = new Map();
    ratings.forEach(r => map.set(String(r.userId), Number(r.value)));
    const vals = Array.from(map.values());
    const sum = vals.reduce((s, v) => s + v, 0);
    return (sum / vals.length).toFixed(1);
  };

  // دانلود فایل (با دامنهٔ درست بک‌اند)
  const handleDownload = async id => {
    if (!user) {
      alert('Please login to download.');
      return;
    }

    try {
      const downloadUrl = `${api.defaults.baseURL}/api/publications/${id}/download-file`;

      const response = await fetch(downloadUrl, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        alert('Download failed');
        return;
      }

      const disposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed');
    }
  };

  const handleRating = async (id, value) => {
    if (!user) {
      alert('Please login to rate.');
      return;
    }

    try {
      const { data } = await api.post(
        `/api/publications/${id}/rating`,
        { value },
        {
          headers: { 'X-CSRF-Token': csrfToken },
          withCredentials: true
        }
      );
      setItems(prev => prev.map(p => (p._id === data._id ? data : p)));
    } catch (err) {
      console.error('Error rating publication', err);
    }
  };

  const handleComment = async (id, text) => {
    if (!user) {
      alert('Please login to comment.');
      return;
    }

    try {
      const { data } = await api.post(
        `/api/publications/${id}/comment`,
        { text },
        {
          headers: { 'X-CSRF-Token': csrfToken },
          withCredentials: true
        }
      );
      setItems(prev => prev.map(p => (p._id === data._id ? data : p)));
    } catch (err) {
      console.error('Error commenting on publication', err);
    }
  };

  return (
    <div className="container py-5">
      <h1 className="h4 mb-4">Publications</h1>

      {/* Filters */}
      <div className="mb-4 row g-3">
        <div className="col-md-3">
          <input
            className="form-control"
            placeholder="Search title..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={categoryId}
            onChange={e => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <input
            className="form-control"
            placeholder="Topic..."
            value={topic}
            onChange={e => {
              setTopic(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={fileFormat}
            onChange={e => {
              setFileFormat(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All formats</option>
            <option value="pdf">PDF</option>
            <option value="docx">DOCX</option>
            <option value="txt">TXT</option>
          </select>
        </div>
        <div className="col-md-3">
          <input
            type="number"
            className="form-control"
            placeholder="Min pages"
            value={minPages}
            onChange={e => {
              setMinPages(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3">
          <input
            type="number"
            className="form-control"
            placeholder="Max pages"
            value={maxPages}
            onChange={e => {
              setMaxPages(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={sortTitle}
            onChange={e => {
              setSortTitle(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Sort by date (default)</option>
            <option value="asc">Title: A → Z</option>
            <option value="desc">Title: Z → A</option>
          </select>
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {perPageOptions.map(n => (
              <option key={n} value={n}>
                {n} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Count banner */}
      <div className="mb-3">
        <div className="alert alert-info text-center fs-6">
          📰 <strong>{total}</strong> publications found
        </div>
      </div>

      {/* List */}
      <div className="row g-4">
        {items.map(p => {
          const catName = p.categoryId?.name || 'Uncategorized';
          const rating = avgRating(p.ratings);

          return (
            <div key={p._id} className="col-md-6">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="row">
                    <div className="col-8">
                      <div className="d-flex justify-content-between align-items-start">
                        <h5 className="mb-2">{p.title}</h5>
                        <span className="badge bg-secondary">{catName}</span>
                      </div>

                      <ul className="list-unstyled mb-2">
                        <li>
                          <strong>Topic:</strong> {p.topic || '-'}
                        </li>
                        <li>
                          <strong>Published:</strong>{' '}
                          {p.publishDate
                            ? new Date(p.publishDate).toLocaleDateString()
                            : 'N/A'}
                        </li>
                        <li>
                          <strong>Pages:</strong> {p.pageCount}
                        </li>
                        <li>
                          <strong>Format:</strong>{' '}
                          {(p.fileFormat || '').toUpperCase()}
                        </li>
                        <li>
                          <strong>Size:</strong>{' '}
                          {formatBytes(p.fileSizeBytes)}
                        </li>
                      </ul>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="text-muted">
                          Avg rating: {rating} ★
                        </span>
                        <button
                          className={`btn btn-sm ${
                            user ? 'btn-primary' : 'btn-outline-secondary'
                          }`}
                          onClick={() =>
                            user
                              ? handleDownload(p._id)
                              : alert('Please login to download.')
                          }
                        >
                          Download
                        </button>
                      </div>

                      {user && (
                        <div className="mb-3">
                          <label className="form-label">Your rating:</label>
                          <div>
                            {[1, 2, 3, 4, 5].map(val => (
                              <span
                                key={val}
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '1.4rem',
                                  color:
                                    (p.ratings.find(
                                      r =>
                                        String(r.userId) ===
                                        String(user._id)
                                    )?.value || 0) >= val
                                      ? 'gold'
                                      : 'gray'
                                }}
                                onClick={() => handleRating(p._id, val)}
                              >
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {user && (
                        <form
                          onSubmit={e => {
                            e.preventDefault();
                            const text = e.target.comment.value;
                            if (text.trim()) handleComment(p._id, text);
                            e.target.reset();
                          }}
                        >
                          <div className="mb-3">
                            <label className="form-label">
                              Leave a comment:
                            </label>
                            <textarea
                              name="comment"
                              className="form-control"
                              rows="2"
                              required
                            />
                          </div>
                          <button className="btn btn-primary btn-sm">
                            Submit
                          </button>
                        </form>
                      )}

                      <ul className="list-group list-group-flush mt-3">
                        {p.comments.map(c => (
                          <li
                            key={c._id || `${c.userId}-${c.createdAt}`}
                            className="list-group-item"
                          >
                            <strong>{c.userName}:</strong> {c.text}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="col-4 d-flex justify-content-center align-items-start">
                      {p.coverUrl && (
                        <img
                          src={p.coverUrl}
                          alt="cover"
                          className="cover-zoom"
                          style={{
                            width: '140px',
                            height: '190px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <p className="text-muted">No publications available</p>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-3">
        <nav>
          <ul className="pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
              <li
                key={pg}
                className={`page-item ${pg === page ? 'active' : ''}`}
              >
                <button
                  className="page-link"
                  onClick={() => setPage(pg)}
                >
                  {pg}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
